import { Injectable, EventEmitter } from '@angular/core';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { AuthService } from 'src/app/auth-module/services';
import { environment } from 'src/environments/environment';

export enum FeatureFlag {
  NorthstarReports = 'northstar-reports',
  PresentationBreadcrumbs = 'presentation-breadcrumbs',
  MoveXpelAndDataDots = 'move-xpel-and-data-dots',
  AddXpelAndDataDotsToPresentationChart = 'add-xpel-and-data-dots-to-presentation-chart',
  NewVSAFlow = 'new-vsa-flow',
  MigrateBugReportDeals = 'migrate-bug-report-deals',
  ToyotaIncentivesImporter = 'toyota-incentive-importer',
  ToyotaResidualsImporter = 'toyota-residual-importer',
}

export interface FeatureFlagsUpdate {
  flag: string;
  value: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsService {
  client: LDClient.LDClient;
  context: LDClient.LDContext;
  private featureFlags: { [key: string]: boolean } = {
    [FeatureFlag.NorthstarReports]: false,
    [FeatureFlag.PresentationBreadcrumbs]: false,
    [FeatureFlag.MoveXpelAndDataDots]: false,
    [FeatureFlag.AddXpelAndDataDotsToPresentationChart]: false,
    [FeatureFlag.NewVSAFlow]: false,
    [FeatureFlag.MigrateBugReportDeals]: false,
    [FeatureFlag.ToyotaIncentivesImporter]: false,
    [FeatureFlag.ToyotaResidualsImporter]: false,
  };

  // EventEmitter that notifies listeners when feature flags are updated
  flagsUpdated = new EventEmitter<FeatureFlagsUpdate>();

  constructor(
    private authService: AuthService
  ) {
    authService.selectUser().subscribe(user => {
      this.context = {
        kind: 'user',
        key: user ? user.id.toString() : 'anonymous',
        name: user ? user.firstName + ' ' + user.lastName : 'Anonymous',
        email: user ? user.email : '',
      }

      this.client = LDClient.initialize(environment.launchDarkly.clientId, this.context);
      
      this.client.on('initialized', function () {
        const allFlags = this.client.allFlags();
        Object.keys(allFlags).forEach(flag => {
          this.featureFlags[flag] = allFlags[flag];
          this.flagsUpdated.emit({ flag, value: allFlags[flag] });
        });
      }.bind(this));

      // Listen for flag changes and emit updates
      this.client.on('change', function (changes: LDClient.LDFlagChangeset) {
        Object.keys(changes).forEach(flag => {
          const newValue = changes[flag].current;
          this.featureFlags[flag] = newValue;
          this.flagsUpdated.emit({ flag, value: newValue });
        });
      }.bind(this));
    });
  }

  getFeatureFlag(flag: string): boolean {
    return this.featureFlags[flag] || false;
  }

  /**
   * Subscribe to feature flag updates
   * @param callback Function to call when any flag is updated
   * @returns Subscription that can be unsubscribed
   */
  onFlagsUpdated(callback: (update: FeatureFlagsUpdate) => void) {
    return this.flagsUpdated.subscribe(callback);
  }

  /**
   * Subscribe to updates for a specific flag
   * @param flag The flag key to listen for
   * @param callback Function to call when the specific flag is updated
   * @returns Subscription that can be unsubscribed
   */
  onFlagUpdated(flag: string, callback: (value: boolean) => void) {
    return this.flagsUpdated.subscribe((update: FeatureFlagsUpdate) => {
      if (update.flag === flag) {
        callback(update.value);
      }
    });
  }
}
